import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ZoomInIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

export const Gallery = () => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const galleryImages = [
    {
      src: '/assets/galeria1.jpg',
      alt: 'Paseo nocturno en el Malecón',
      caption: 'El majestuoso Malecón 2000 visto desde el río',
    },
    {
      src: '/assets/galeria2.jpg',
      alt: 'Experiencia grupal en lancha',
      caption: 'Momentos inolvidables con familia y amigos',
    },
    {
      src: '/assets/galeria3.jpg',
      alt: 'Diversión y aventura',
      caption: 'Risas y alegría en cada recorrido',
    },
    {
      src: '/assets/guayaquil.jpg',
      alt: 'Atardecer en Guayaquil',
      caption: 'Atardeceres que pintan el cielo de la Perla del Pacífico',
    },
    {
      src: '/assets/malecon-2000.jpg',
      alt: 'Vista del Malecón 2000',
      caption: 'El impresionante skyline y Malecón 2000',
    },
    {
      src: '/assets/rio-guayas.jpg',
      alt: 'Río Guayas al atardecer',
      caption: 'Momentos mágicos que recordarás para siempre',
    },
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return;

      if (e.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        setSelectedImageIndex(selectedImageIndex - 1);
      } else if (e.key === 'ArrowRight' && selectedImageIndex < galleryImages.length - 1) {
        setSelectedImageIndex(selectedImageIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, galleryImages.length]);

  // Prevent body scroll when modal is open and hide header/whatsapp
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.body.style.overflow = 'hidden';
      // Ocultar header y botón de WhatsApp
      const header = document.querySelector('header');
      const whatsappBtn = document.getElementById('whatsapp-button');
      if (header) (header as HTMLElement).style.display = 'none';
      if (whatsappBtn) whatsappBtn.style.display = 'none';
    } else {
      document.body.style.overflow = 'unset';
      // Mostrar header y botón de WhatsApp
      const header = document.querySelector('header');
      const whatsappBtn = document.getElementById('whatsapp-button');
      if (header) (header as HTMLElement).style.display = '';
      if (whatsappBtn) whatsappBtn.style.display = '';
    }
    return () => {
      document.body.style.overflow = 'unset';
      const header = document.querySelector('header');
      const whatsappBtn = document.getElementById('whatsapp-button');
      if (header) (header as HTMLElement).style.display = '';
      if (whatsappBtn) whatsappBtn.style.display = '';
    };
  }, [selectedImageIndex]);

  return (
    <section id="galeria" className="section bg-white relative overflow-hidden">
      {/* Decorative elements animados */}
      <motion.div
        className="absolute top-40 right-20 w-72 h-72 bg-turquoise/5 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-96 h-96 bg-navy/5 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.1, 1],
          x: [0, -30, 0],
          y: [0, 50, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="container relative z-10">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="inline-block text-turquoise font-medium mb-3 tracking-wider uppercase text-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            GALERÍA
          </motion.span>
          
          <motion.h2
            className="font-playfair text-3xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Capturando{' '}
            <span className="text-turquoise relative inline-block">
              Momentos Inolvidables
            </span>
          </motion.h2>

          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-turquoise to-navy mx-auto mb-8"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />

          <motion.p
            className="max-w-2xl mx-auto text-lg text-gray-600"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            Explora nuestra colección de imágenes y descubre la belleza del río
            Guayas y los momentos especiales que creamos para nuestros visitantes
          </motion.p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              className="relative overflow-hidden rounded-xl aspect-[4/3] group cursor-pointer shadow-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              whileHover={{ scale: 1.03, zIndex: 10 }}
              onClick={() => setSelectedImageIndex(index)}
            >
              {/* Image */}
              <motion.img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover"
                loading="lazy"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.4 }}
              />

              {/* Overlay */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent flex flex-col items-center justify-end p-4"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Zoom Icon */}
                <motion.div
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
                  initial={{ scale: 0, rotate: -180 }}
                  whileHover={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ZoomInIcon className="w-5 h-5 text-white" />
                </motion.div>

                {/* Caption */}
                <motion.p
                  className="text-white text-sm md:text-base font-medium text-center"
                  initial={{ y: 20, opacity: 0 }}
                  whileHover={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  {image.caption}
                </motion.p>
              </motion.div>

              {/* Border Effect */}
              <motion.div
                className="absolute inset-0 border-2 border-turquoise/0 rounded-xl pointer-events-none"
                whileHover={{ borderColor: 'rgba(0, 168, 204, 0.6)' }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          ))}
        </div>

        {/* Image Modal with AnimatePresence - LIGHTBOX MEJORADO */}
        <AnimatePresence>
          {selectedImageIndex !== null && (
            <motion.div
              className="fixed inset-0 bg-black/98 backdrop-blur-xl z-[99999] flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setSelectedImageIndex(null)}
            >
              {/* Close Button */}
              <motion.button
                className="absolute top-4 right-4 md:top-8 md:right-8 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-navy hover:bg-turquoise hover:text-white transition-all z-10 shadow-2xl border-2 border-white/20"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(null);
                }}
                aria-label="Cerrar galería"
              >
                <XIcon className="h-7 w-7" />
              </motion.button>

              {/* Previous Button */}
              <motion.button
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-navy hover:bg-turquoise hover:text-white transition-all z-10 shadow-2xl disabled:opacity-20 disabled:cursor-not-allowed border-2 border-white/20"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                whileHover={{ scale: 1.1, x: -5 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedImageIndex > 0) {
                    setSelectedImageIndex(selectedImageIndex - 1);
                  }
                }}
                disabled={selectedImageIndex === 0}
                aria-label="Imagen anterior"
              >
                <ChevronLeftIcon className="h-8 w-8" />
              </motion.button>

              {/* Next Button */}
              <motion.button
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-navy hover:bg-turquoise hover:text-white transition-all z-10 shadow-2xl disabled:opacity-20 disabled:cursor-not-allowed border-2 border-white/20"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 50, opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                whileHover={{ scale: 1.1, x: 5 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedImageIndex < galleryImages.length - 1) {
                    setSelectedImageIndex(selectedImageIndex + 1);
                  }
                }}
                disabled={selectedImageIndex === galleryImages.length - 1}
                aria-label="Imagen siguiente"
              >
                <ChevronRightIcon className="h-8 w-8" />
              </motion.button>

              {/* Image Counter */}
              <motion.div
                className="absolute top-4 left-4 md:top-8 md:left-8 px-5 py-2 bg-white/90 backdrop-blur-sm rounded-full text-navy font-semibold shadow-2xl border-2 border-white/20"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {selectedImageIndex + 1} / {galleryImages.length}
              </motion.div>

              {/* Image Container */}
              <motion.div
                className="relative max-w-7xl w-full px-4 md:px-20"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedImageIndex}
                    className="relative"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.img
                      src={galleryImages[selectedImageIndex].src}
                      alt={galleryImages[selectedImageIndex].alt}
                      className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl mx-auto"
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* Image Caption */}
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-2xl"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <p className="text-white text-center text-lg font-medium">
                        {galleryImages[selectedImageIndex].caption}
                      </p>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>

              {/* Keyboard Navigation Hint */}
              <motion.div
                className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white/60 text-sm"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <span className="hidden md:inline">Usa las flechas del teclado o los botones para navegar</span>
                <span className="md:hidden">Desliza para navegar</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};